import React, { useRef, useState } from "react";
import { MdOutlineFileUpload, MdInsertDriveFile, MdCheckCircle } from "react-icons/md";

import TextMessage from "../text-message/text-message";
import { uploadDocument, MAX_UPLOAD_BYTES } from "../../../../services/doc-capture";
import { useAppInfo } from "../../../../helpers/hooks/common-hook";

const DEFAULT_FILE_TYPES = ['pdf', 'docx', 'csv', 'txt', 'md', 'png', 'jpg'];

// Sent alongside the answer so the runtime knows this turn is a document
// upload and skips the text-answer validation path.
const DOC_CAPTURE_ACTION_TYPE = 'upload_document';

const formatFileSize = bytes => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function DocCapture(props) {
  const { message = {}, onInvokeAction, sessionId, disabled } = props;
  const { appId, accessKey } = useAppInfo();

  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [error, setError] = useState('');

  const configuredTypes = Array.isArray(message.allowedFileTypes)
    ? message.allowedFileTypes.filter(type => typeof type === 'string')
        .map(type => type.trim().toLowerCase().replace(/^\./, '')).filter(Boolean)
    : [];
  const allowedFileTypes = configuredTypes.length ? configuredTypes : DEFAULT_FILE_TYPES;

  const accept = allowedFileTypes.map(type => `.${type}`).join(',');
  const displayText = message.displayText || '';

  // Once the answer is sent the step is done; the control stays visible as a
  // record of what was uploaded but must not accept a second file, which would
  // answer a question the workflow has already moved past.
  const isDone = !!uploadedFile;
  const isDisabled = disabled || uploading || isDone;

  const onSelectFile = async event => {
    const file = event.target.files && event.target.files[0];
    // Let the same file be picked again after a failure.
    event.target.value = '';
    if (!file || isDisabled) return;

    const fileExt = (file.name || '').split('.').pop().toLowerCase();
    if (!allowedFileTypes.includes(fileExt)) {
      setError(`Please upload one of: ${allowedFileTypes.join(', ')}`);
      return;
    }

    if (file.size > MAX_UPLOAD_BYTES) {
      setError(`That file is too large. The limit is ${formatFileSize(MAX_UPLOAD_BYTES)}.`);
      return;
    }

    setError('');
    setUploading(true);

    try {
      const result = await uploadDocument(appId, accessKey, sessionId, file);
      if (!result || result.error) {
        setError(result && result.error === 'too_large'
          ? `That file is too large. The limit is ${formatFileSize(MAX_UPLOAD_BYTES)}.`
          : "That upload didn't go through. Please try again.");
        return;
      }

      await onInvokeAction(file.name, {
        actionType: DOC_CAPTURE_ACTION_TYPE,
        slot: message.slot,
        fileKey: result.fileKey,
        fileName: result.fileName,
        size: result.size,
        title: file.name
      });
      setUploadedFile({ name: file.name, size: file.size });
    } catch {
      setError("We couldn't submit your document. Please choose the file to try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <section className="chat-document" aria-label="Document upload" aria-busy={uploading}>
      {displayText && <TextMessage {...props} message={displayText} />}
      <div className="chat-document-card">
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          aria-label="Choose document"
          className="hidden"
          disabled={isDisabled}
          onChange={onSelectFile}
        />
        {isDone ? (
          <div className="chat-document-success" role="status">
            <span className="chat-document-icon"><MdInsertDriveFile size={22} /></span>
            <div className="min-w-0 flex-1">
              <p className="chat-document-filename" title={uploadedFile.name}>{uploadedFile.name}</p>
              <p className="chat-document-meta">{formatFileSize(uploadedFile.size)} · Document submitted</p>
            </div>
            <MdCheckCircle size={20} className="shrink-0" aria-hidden="true" />
          </div>
        ) : (
          <button
            type="button"
            disabled={isDisabled}
            onClick={() => fileInputRef.current?.click()}
            className="chat-document-upload"
          >
            <span className="chat-document-icon"><MdOutlineFileUpload size={24} /></span>
            <span className="font-semibold">{uploading ? 'Uploading document…' : 'Upload a document'}</span>
            <span className="chat-document-meta">Choose a file · Up to {formatFileSize(MAX_UPLOAD_BYTES)}</span>
            <span className="chat-document-types">{allowedFileTypes.join(', ').toUpperCase()}</span>
            {!uploading && <span className="chat-document-cta">Choose file <span aria-hidden="true">↗</span></span>}
          </button>
        )}
        {uploading && <p className="sr-only" role="status">Uploading document. Please wait.</p>}
        {error && <p className="chat-error" role="alert">{error}</p>}
      </div>
    </section>
  );
}
